// Global gemountet (extraGlobals): zeigt fällige Erinnerungen als Toast – je Erinnerung/Zeitpunkt einmal pro Sitzung.
import type { ReminderDto } from '@lagerfeuer/shared';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { useAuthStore } from '../../../store/auth';
import { remindersApi } from '../api';
import { DAY } from '../scheduled/time';
import { markReminderShown, remindersListKey, reminderToastData, showReminderToast, wasReminderShown } from './notify';

/** Abfrage-Intervall der offenen Erinnerungen. */
export const REMINDER_POLL_MS = 60_000;

interface Planned {
  at: string;
  timer: ReturnType<typeof setTimeout>;
}

export function ReminderWatcher() {
  const qc = useQueryClient();
  const userId = useAuthStore((s) => s.user?.id ?? null);
  const query = useQuery({
    queryKey: remindersListKey(false),
    queryFn: () => remindersApi.list({ done: false }),
    enabled: userId !== null,
    refetchInterval: REMINDER_POLL_MS,
    refetchIntervalInBackground: true,
    refetchOnWindowFocus: true,
  });
  const reminders = userId !== null ? query.data : undefined;
  const planned = useRef(new Map<string, Planned>());

  useEffect(() => {
    const timers = planned.current;
    const now = Date.now();
    const live = new Set<string>();
    for (const r of reminders ?? []) {
      if (r.done || wasReminderShown(r.id, r.remindAt)) continue;
      const due = new Date(r.remindAt).getTime();
      if (Number.isNaN(due) || due - now > DAY) continue;
      live.add(r.id);
      const existing = timers.get(r.id);
      if (existing?.at === r.remindAt) continue;
      if (existing) clearTimeout(existing.timer);
      const timer = setTimeout(
        () => {
          timers.delete(r.id);
          if (wasReminderShown(r.id, r.remindAt)) return;
          // Neuesten Stand aus dem Cache nehmen (Notiz kann sich geändert haben).
          const latest = qc.getQueryData<ReminderDto[]>(remindersListKey(false))?.find((x) => x.id === r.id) ?? r;
          if (latest.done || latest.remindAt !== r.remindAt) return;
          markReminderShown(r.id, r.remindAt);
          showReminderToast(qc, reminderToastData(latest));
        },
        Math.max(0, due - now),
      );
      timers.set(r.id, { at: r.remindAt, timer });
    }
    for (const [id, p] of timers) {
      if (!live.has(id)) {
        clearTimeout(p.timer);
        timers.delete(id);
      }
    }
  }, [reminders, qc]);

  useEffect(() => {
    const timers = planned.current;
    return () => {
      for (const p of timers.values()) clearTimeout(p.timer);
      timers.clear();
    };
  }, []);

  return null;
}
